// Builds a ToastComponent item and drops it in the top-right viewport. Client-side callers (a grid
// action, a panel mutation) have no server round-trip to render a flash from, so they compose the
// same markup the component renders and let its own Stimulus controller take over dismissal.
export type ToastType = "success" | "danger";

// BEM class base. NOT the Stimulus identifier — that is path-derived and repeats the file
// segment (see ToastComponent::CONTROLLER). Using the base as the identifier registers nothing,
// leaving a toast with a dead close button that never auto-dismisses.
const TOAST_BASE = "application--toolkit--toast-component";
const TOAST_CONTROLLER = "application--toolkit--toast-component--toast-component";
const DEFAULT_DURATION = 6000;

export function showToast(message: string, type: ToastType, duration = DEFAULT_DURATION): void {
  const viewport = document.getElementById("toast-viewport-top_right");
  if (!viewport) return;

  const el = document.createElement("div");
  el.id = `toast-${Math.random().toString(36).slice(2)}`;
  el.className = `${TOAST_BASE}__item ${TOAST_BASE}__item--${type} toast--entering`;
  el.setAttribute("role", type === "danger" ? "alert" : "status");
  el.setAttribute("aria-live", type === "danger" ? "assertive" : "polite");
  el.setAttribute("aria-atomic", "true");
  el.setAttribute("data-controller", TOAST_CONTROLLER);
  el.setAttribute(`data-${TOAST_CONTROLLER}-duration-value`, String(duration));
  el.setAttribute(`data-${TOAST_CONTROLLER}-auto-dismiss-value`, "true");
  el.setAttribute(`data-${TOAST_CONTROLLER}-target`, "toast");

  const icon = document.createElement("div");
  icon.className = `${TOAST_BASE}__icon`;
  const iconGlyph = document.createElement("i");
  iconGlyph.className = type === "danger" ? "fa fa-times-circle" : "fa fa-check-circle";
  iconGlyph.setAttribute("aria-hidden", "true");
  icon.appendChild(iconGlyph);
  el.appendChild(icon);

  const content = document.createElement("div");
  content.className = `${TOAST_BASE}__content`;
  const messageEl = document.createElement("div");
  messageEl.className = `${TOAST_BASE}__message`;
  messageEl.textContent = message;
  content.appendChild(messageEl);
  el.appendChild(content);

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = `${TOAST_BASE}__close`;
  closeButton.setAttribute("aria-label", "Dismiss notification");
  closeButton.setAttribute("data-action", `click->${TOAST_CONTROLLER}#dismiss`);
  const closeIcon = document.createElement("i");
  closeIcon.className = "fa fa-times";
  closeIcon.setAttribute("aria-hidden", "true");
  closeButton.appendChild(closeIcon);
  el.appendChild(closeButton);

  const progress = document.createElement("div");
  progress.className = `${TOAST_BASE}__progress`;
  progress.setAttribute(`data-${TOAST_CONTROLLER}-target`, "progressBar");
  progress.setAttribute("role", "progressbar");
  progress.setAttribute("aria-label", "Time remaining");
  progress.setAttribute("aria-valuemin", "0");
  progress.setAttribute("aria-valuemax", "100");
  progress.setAttribute("aria-valuenow", "100");
  el.appendChild(progress);

  viewport.appendChild(el);
}
